<template>
  <div
    @click.stop="() => false"
    :class="{ 'notifications-list': true, 'is-visible': this.isVisible, 'is-empty': !notifications.length, 'is-changing-state': this.markAsReadInProgress }">
    <div
      v-if="!notifications.length"
      class="notifications-list-empty-state">
      {{ $t('notifications.emptyState') }}
    </div>

    <div class="notifications-list-tabs">
      <div
        @click="selectedTab = 'all'"
        :class="{ 'notifications-list-tab': true, 'is-active': selectedTab === 'all' }">
        {{ $t('notifications.all') }}
      </div>

      <div
        @click="selectedTab = 'unread'"
        :class="{ 'notifications-list-tab': true, 'is-active': selectedTab === 'unread' }">
        {{ $t('notifications.onlyUnread', { unreadCount: unreadCount }) }}
      </div>
    </div>

    <div
      v-if="notifications.length"
      class="notifications-list-wrapper">
      <notifications-item
        v-for="notification of notifications"
        :key="'notification-' + notification.id"
        :notificationData="notification" />
    </div>

    <a
      v-if="$store.state.global.notifications.length"
      @click="markAsReadAllNotifications()">
      <template v-if="!markAsReadInProgress">
        {{ $t('notifications.markAllAsRead') }}
      </template>
      <template v-else>
        {{ $t('notifications.pleaseWait') }}
      </template>
    </a>
  </div>
</template>

<script>
import ErrorUtils from './../helpers/ErrorUtils.js';
import NotificationsItem from './NotificationsItem';

export default {
  name: 'notifications',
  components: {
    'notifications-item': NotificationsItem
  },
  data () {
    return {
      isVisible: false,
      markAsReadInProgress: false,
      selectedTab: 'unread'
    };
  },
  computed: {
    notifications () {
      if (this.selectedTab === 'all') {
        return this.$store.state.global.notifications;
      }

      return this.$store.state.global.notifications.filter(n => !n.readed_at);
    },
    unreadCount () {
      return this.$store.state.global.notifications.filter(n => !n.readed_at).length;
    }
  },
  mounted () {
    this.$bus.$on('body-clicked', this.hide);
    this.$bus.$on('toggle-app-notifications', this.toggle);
    this.$bus.$on('hide-app-notifications', this.hide);
  },
  methods: {
    hide () {
      this.isVisible = false;
    },
    toggle () {
      this.isVisible = !this.isVisible;

      if (this.isVisible) {
        this.selectedTab = 'unread';
      }
    },
    markAsReadAllNotifications () {
      if (this.markAsReadInProgress) {
        return;
      }

      this.markAsReadInProgress = true;

      this.$http.post('/panel/notifications/set-readed', {
        type: 1
      }).then(response => {
        this.$store.commit('global/markAsReadAllNotifications');
        this.markAsReadInProgress = false;
        this.$bus.$emit('update-favicon');
      }).catch(error => {
        ErrorUtils.handleSubmitError(error, '/panel/notifications/set-readed', this.$router, this.$bus, this.markAsReadAllNotifications.bind(this));
        this.markAsReadInProgress = false;
      });
    }
  },
  destroy () {
    this.$bus.$off('body-clicked', this.hide);
    this.$bus.$off('toggle-app-notifications', this.toggle);
    this.$bus.$off('hide-app-notifications', this.hide);
  }
}
</script>

<style lang="scss" scoped>
@import '../../assets/scss/variables.scss';

.notifications {
  &-list {
    background: $color-bg-lightest;
    border: 1px solid $color-border;
    border-bottom: none;
    border-top: none;
    border-radius: 0;
    box-shadow: 0 0 25px rgba(0, 0, 0, 0.25);
    height: calc(100% - 70px);
    opacity: 0;
    padding: 3.6rem 0 5rem;
    pointer-events: none;
    position: fixed;
    right: 0;
    text-align: center;
    top: 70px;
    transform: translateX(50px);
    transform-origin: right center;
    transition: all .3s ease-out;
    width: 400px;
    z-index: 9999;

    &-tabs {
      align-items: center;
      border-bottom: 1px solid $color-border;
      display: flex;
      height: 3.6rem;
      padding: 0 1.6rem;
      position: absolute;
      top: 0;
      width: 100%;
    }

    &-tab {
      align-items: center;
      color: $color-text-lightest;
      cursor: pointer;
      display: flex;
      font-size: 1.2rem;
      font-weight: 600;
      height: 3.6rem;
      margin-right: 1.6rem;
      position: relative;
      text-transform: uppercase;

      &.is-active {
        color: $color-text-dark;

        &:after {
          background: $color-notice;
          bottom: 0;
          content: '';
          height: 3px;
          left: 0;
          position: absolute;
          width: 100%;
        }
      }
    }

    &.is-changing-state {
      .notifications-item {
        opacity: .5;
        pointer-events: none;
      }
    }

    &.is-visible {
      opacity: 1;
      pointer-events: auto;
      right: 0;
      top: 70px;
      transform: translateX(0);
    }

    &-wrapper {
      height: calc(100% - 8.6rem);
      left: 0;
      overflow: auto;
      -webkit-overflow-scrolling: touch;
      padding: 0;
      position: absolute;
      right: 0;
      top: 3.6rem;
      width: 100%;
    }

    & > a {
      bottom: 1.5rem;
      cursor: pointer;
      font-size: 1.3rem;
      left: 2rem;
      position: absolute;
      right: 2rem;
      width: calc(100% - 4rem);
    }

    &.is-empty {
      align-items: center;
      display: flex;
      justify-content: center;

      .notifications-list-empty-state {
        color: $color-text-lightest;
        font-weight: 600;
      }
    }
  }
}

@media (max-width: 640px) {
  .notifications-list {
    border-radius: $border-radius-small;
    box-shadow: 0 0 25px rgba(0, 0, 0, 0.25);
    height: calc(100% - 10rem);
    left: 1rem;
    position: fixed;
    top: 5.8rem;
    width: calc(100vw - 2rem);

    &.is-visible {
      height: calc(100% - 10rem);
      left: 1rem;
      top: 5.8rem;
      width: calc(100vw - 2rem);
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/ui/Notifications.vue